import { ChangeEvent, KeyboardEvent, MouseEvent, useState } from "react";
import { Add as AddIcon, Create as EditIcon, Delete as DeleteIcon } from "../../_app/components/icons";
import { UIConfirm, UIIconButton, UITextField, UITooltip, UITypography } from "../../_app/components";
import { CostCentre } from "../types";
import { createStylesheet } from "../../_app/utils/styles";

interface Props {
  hasAdd?: boolean;
  costCentre: CostCentre;
  handleSubmit?: Function;
  handleDelete?: Function;
  handleUpdate?: Function;
  [key: string]: any;
}

function CostCentreActionButtons({
  hasAdd = true,
  costCentre,
  handleSubmit = () => {},
  handleDelete = () => {},
  handleUpdate = () => {},
}: Props) {
  const classes = useStyles();
  const [warning, setWarning] = useState(false);
  const [addWarning, setAddWarning] = useState(false);
  const [editWarning, setEditWarning] = useState(false);
  const [data, setData] = useState({ ...costCentre, newName: null });

  const handleCostCentreSubmit = () => {
    return handleSubmit({
      ...costCentre,
      id: "",
      parent: data?.id,
      name: data?.newName,
      subCostCentres: [],
    });
  };

  const handleCostCentreCancel = () => {
    setData({ ...costCentre, newName: null });
  };

  const handleCostCentreUpdate = () => {
    const { newName, ...edited } = data;
    return handleUpdate(edited);
  };

  const handleCostCentreDelete = () => {
    return handleDelete(data?.id);
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const key = e.target.name;
    const val = e.target.value;
    setData({ ...data, [key]: val });
  };

  return (
    <div
      className={classes.right}
      role="button"
      tabIndex={0}
      onClick={(ev: MouseEvent<HTMLDivElement>) => ev?.stopPropagation()}
      onKeyDown={(ev: KeyboardEvent<HTMLDivElement>) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.stopPropagation();
        }
      }}
    >
      {Boolean(hasAdd) && (
        <>
          <UITooltip title="Add sub cost centre" arrow>
            <UIIconButton
              component={"span"}
              data-cy="cost-centre-add"
              size="small"
              color="primary"
              aria-label={`Add sub cost centre under ${costCentre?.name}`}
              onClick={() => setAddWarning(true)}
            >
              <AddIcon fontSize="small" />
            </UIIconButton>
          </UITooltip>
          <UIConfirm
            title={`Add sub cost centre under "${costCentre?.name}"`}
            onSubmitTitle="Add"
            open={addWarning}
            setOpen={setAddWarning}
            onConfirm={handleCostCentreSubmit}
            onCancel={handleCostCentreCancel}
            disableOnSubmit={!data?.newName}
          >
            <UITextField
              name="newName"
              label="Cost Centre Name"
              type="text"
              value={data?.newName || ""}
              onChange={onInputChange}
              variant="outlined"
              size="small"
              fullWidth
            />
          </UIConfirm>
        </>
      )}
      <UITooltip title="Rename" arrow>
        <UIIconButton
          data-cy="cost-centre-edit"
          size="small"
          aria-label={`Rename cost centre ${costCentre?.name}`}
          onClick={() => setEditWarning(true)}
          component={"span"}
        >
          <EditIcon fontSize="small" />
        </UIIconButton>
      </UITooltip>
      <UIConfirm
        title={`Rename cost centre "${costCentre?.name}"`}
        onSubmitTitle="Save"
        open={editWarning}
        setOpen={setEditWarning}
        onConfirm={handleCostCentreUpdate}
        disableOnSubmit={!data?.name}
      >
        <UITextField
          name="name"
          label="Cost Centre Name"
          type="text"
          defaultValue={costCentre?.name || ""}
          onChange={onInputChange}
          variant="outlined"
          size="small"
          fullWidth
        />
      </UIConfirm>
      <UITooltip title="Delete" arrow>
        <UIIconButton
          data-cy="cost-centre-delete"
          size="small"
          color="error"
          aria-label={`Delete cost centre ${costCentre?.name}`}
          onClick={() => setWarning(true)}
          component={"span"}
        >
          <DeleteIcon fontSize="small" />
        </UIIconButton>
      </UITooltip>
      <UIConfirm title="Delete cost centre" open={warning} setOpen={setWarning} onConfirm={handleCostCentreDelete} isDelete>
        <UITypography>Are you sure you want to delete "{costCentre?.name}"?</UITypography>
      </UIConfirm>
    </div>
  );
}

const useStyles = createStylesheet((theme) => ({
  right: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(0.5),
    alignItems: "center",
  },
}));

export default CostCentreActionButtons;
